import { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { acGetAllPlans, acGetPlan } from '../../store/actions'
import { DataTable } from '../default'
import { CreatePlanModal, UpdatePlanModal, DeletePlanModal } from '../modals'

/**
 * @description PlansTable component displays a table of plans and provides functionality for creating, updating, and deleting plans.
 *
 * @author Maria Eduarda Franke
 * @returns {JSX.Element} The PlansTable component
 */
const PlansTable = () => {

    const dispatch = useDispatch()

    const [openCreateModal, setOpenCreateModal] = useState(false)
    const [openUpdateModal, setOpenUpdateModal] = useState(false)
    const [openDeleteModal, setOpenDeleteModal] = useState(false)

    // Get All Plans
    const allPlans = useSelector(state => state.plans.allPlans)
    const isLoadingAllPlans = useSelector(state => state.plans.isLoadingAllPlans)
    const hasErrorAllPlans = useSelector(state => state.plans.hasErrorAllPlans)
    // Create, Update and Delete Plan
    const selectedPlan = useSelector(state => state.plans.selectedPlan)
    const isCreatingPlan = useSelector(state => state.plans.isCreatingPlan)
    const isUpdatingPlan = useSelector(state => state.plans.isUpdatingPlan)
    const isDeletingPlan = useSelector(state => state.plans.isDeletingPlan)

    useEffect(() => {
        dispatch(acGetAllPlans())
            .catch(error => console.error(error))
        // eslint-disable-next-line
    }, [])

    const columns = [
        {
            accessorKey: 'plan_id',
            header: 'ID',
            enableEditing: false,
            size: 80
        },
        {
            accessorKey: 'name',
            header: 'Nome'
        },
        {
            accessorKey: 'monthly_cost',
            header: 'Valor Mensal',
            Cell: ({ cell }) => cell.getValue()?.toLocaleString?.('pt-BR', {
                style: 'currency',
                currency: 'BRL',
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            })
        },
        {
            accessorKey: 'requests_qty',
            header: 'Qtde. Consultas'
        }
    ]

    // Handle Create Action
    const onCreate = () => {
        setOpenCreateModal(!openCreateModal)
    }

    // Handle Update Action
    const onUpdate = (row) => {
        dispatch(acGetPlan(row.original.plan_id))
            .then(() => {
                setOpenUpdateModal(!openUpdateModal)
            })
            .catch(error => console.error(error))
    }

    // Handle Delete Action
    const onDelete = (row) => {
        dispatch(acGetPlan(row.original.plan_id))
            .then(() => {
                setOpenDeleteModal(!openDeleteModal)
            })
            .catch(error => console.error(error))
    }

    return (
        <>
            <CreatePlanModal
                open={openCreateModal}
                onClose={() => setOpenCreateModal(!openCreateModal)}
            />
            <UpdatePlanModal
                open={openUpdateModal}
                onClose={() => setOpenUpdateModal(!openUpdateModal)}
                selectedPlan={selectedPlan}
            />
            <DeletePlanModal
                open={openDeleteModal}
                onClose={() => setOpenDeleteModal(!openDeleteModal)}
                selectedPlan={selectedPlan}
            />
            <DataTable
                resource='Plano'
                gender='masc'
                tableType='complete'
                columns={columns}
                data={allPlans}
                onCreate={onCreate}
                onUpdate={onUpdate}
                onDelete={onDelete}
                isLoading={isLoadingAllPlans}
                isSaving={isCreatingPlan || isUpdatingPlan || isDeletingPlan}
                showAlertBanner={hasErrorAllPlans}
            />
        </>
    )

}

export default PlansTable